// components/UserEditModal.tsx
import { User} from '../contexts/AuthProvider';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthProvider';
import { useLocale } from '../contexts/LocaleProvider';
import Environment from '../utility/environment';
import { parsePhotoUrl } from '../utility/parsers';
import { PhoneInput, PhoneInputValue } from './PhoneInput';
import {
  Modal,
  Button,
  TextInput,
  Group,
  Text,
  Image,
  Stack,
  Select,
  Switch,
} from '@mantine/core';
import { sendPhoto } from '../utility/files';
import { useTranslation } from 'react-i18next';
import { AppLocale, LOCALE_LABELS, SUPPORTED_LOCALES, isAppLocale } from '../utility/locale';

interface UserEditModalProps {
  opened: boolean;
  onClose: () => void;
  user: User | null;
}

type UserInDb = {
  uuid: string;
  enterprise_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  role: string;
  cargo: string;
  username: string;
  photo: string | File;
  status: string;
  locale: AppLocale;
  email_notifications_enabled: boolean;
};

const initialForm: UserInDb = {
  uuid: '',
  enterprise_id: '',
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  role: '',
  cargo: '',
  username: '',
  photo: '',
  status: '',
  locale: 'en',
  email_notifications_enabled: false,
};

function mapUserFromApi(data: Record<string, unknown>): UserInDb {
  const localeValue = typeof data.locale === 'string' ? data.locale : '';
  return {
    uuid: String(data.id ?? ''),
    enterprise_id: String(data.enterprise_id ?? ''),
    first_name: String(data.first_name ?? ''),
    last_name: String(data.last_name ?? ''),
    email: String(data.email ?? ''),
    phone_number: String(data.phone_number ?? ''),
    role: String(data.role ?? ''),
    cargo: String(data.cargo ?? ''),
    username: String(data.username ?? ''),
    photo: String(data.photo ?? ''),
    status: String(data.status ?? ''),
    locale: isAppLocale(localeValue) ? localeValue : 'en',
    email_notifications_enabled: Boolean(data.email_notifications_enabled),
  };
}

function mapAuthUserFallback(user: User): UserInDb {
  const nameParts = (user.name || '').trim().split(/\s+/);
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ');
  return {
    ...initialForm,
    uuid: user.id,
    first_name: firstName,
    last_name: lastName,
    email: user.email ?? '',
    role: user.role ?? '',
    cargo: user.cargo ?? '',
    username: user.username ?? '',
    photo: user.photo ?? '',
    locale: isAppLocale(user.locale) ? user.locale : 'en',
  };
}

type FileUploadByImageProps = {
    user: UserInDb;
    setUserForm: React.Dispatch<React.SetStateAction<UserInDb>>;
  };

 export const FileUploadByImage: React.FC<FileUploadByImageProps> = ({ user, setUserForm }) => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const handleImageClick = () => {
      fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];

      if (file) {
        console.log("Arquivo selecionado:", file.name);
        setUserForm((prevUserForm) => ({ ...prevUserForm, photo: file }));
        // Aqui você pode fazer upload, preview, etc.
      }
    };

    const parsePhoto = (photo: string | File) => {
      if (typeof photo === 'string') {
        return parsePhotoUrl(photo);
      }
      return URL.createObjectURL(photo);
    };

    return (
      <div>
        {/* Imagem clicável */}
        <Image
          src={parsePhoto(user.photo)}// Coloque a URL ou import da sua imagem
          alt="Upload"
          onClick={handleImageClick}
          width={200}
          height={200}
          style={{
            cursor: "pointer",
            objectFit: "cover",
            borderRadius: "50%" // opcional, caso queira redonda
          }}
        />

        {/* Input de arquivo invisível */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          accept="image/*" // Ou qualquer outro tipo, como application/pdf
        />
      </div>
    );
  };

export const UserEditModal = ({ opened, onClose, user}: UserEditModalProps) => {
  const { accessToken } = useAuth();
  const { setLocale } = useLocale();
  const { t } = useTranslation();
  const [userInDb, setUserInDb] = useState<UserInDb>(initialForm);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const [emailSent, setEmailSent] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState<string | undefined>(undefined);

  const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

  const nameLength = 50;

  useEffect(() => {
    setEmailSent(false);
    setNotificationMessage(undefined);
    if (!user || !opened) {
      return;
    }

    setUserInDb(mapAuthUserFallback(user));

    fetch(`${Environment.VITE_API_URL}/api/v1/user/${user.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) {
          return;
        }
        const data = await res.json();
        setUserInDb(mapUserFromApi(data));
      })
      .catch((error) => {
        console.error(error);
      });
  }, [opened, user, accessToken]);

  const validateEmail = async (email: string) => {
    if (!emailRegex.test(email)) {
      return; // Falha local, não precisa ir ao backend
    }

    try {
      const res = await fetch(`${Environment.VITE_API_URL}/api/v1/validate_email/${email}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        if (data?.detail === "Email already exists") {
          setEmailError(t('settings.emailAlreadyExists'));
          return false
        } else {
          setEmailError(t('settings.emailValidationError'));
          return false
        }
      } else {
        setEmailError(undefined); // Username válido
        return true
      }
    } catch (error) {
      console.error(error);
      setEmailError(t('settings.connectionError'));
      return false
    }
  };

  const formatUserForCreation = (contact: UserInDb) => {
    return {
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: contact.email,
        role: contact.role,
        cargo: contact.cargo,
        username: contact.username,
        photo: contact.photo,
        status: contact.status,
        phone_number: contact.phone_number,
        locale: contact.locale,
        email_notifications_enabled: contact.email_notifications_enabled,
    };
  };

  const handleResetPassword = async () => {
      // Simula o envio do e-mail de recuperação

      try {
        const response = await fetch(`${Environment.VITE_API_URL}/api/v1/forgot_password/${userInDb?.email}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.message) {
            setEmailSent(true);
            setNotificationMessage(t('settings.passwordResetSent'));
          }

        }

      } catch (error) {
        console.error("ERRO INTERNO");
        setEmailSent(false);
        setNotificationMessage(t('settings.passwordResetFailed'));
      }


  }

  const updateUser = async () => {
    const uploadedPhotoUrl = await sendPhoto(userInDb.photo, accessToken as string);
    var userPayload

    if (uploadedPhotoUrl) {
        userPayload = {
        ...userInDb,
        photo: uploadedPhotoUrl, // Aqui garantimos que é string
        }
        } else{
        userPayload = {
            ...userInDb,
        }
    }

    fetch(`${Environment.VITE_API_URL}/api/v1/users/${userInDb?.uuid}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'include',
      body: JSON.stringify(formatUserForCreation(userPayload)),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log(data);
        setLocale(userInDb.locale);
        onClose();
      })
      .catch((error) => {
        console.error(error);
      })
  };

  const handleSave = async() => {
    await updateUser();
    onClose();
    window.location.reload();
  };

  return (

    <Modal opened={opened} onClose={onClose} title={t('settings.title')} centered pt="xs"  size="xl">

      <Group spacing="md"
            mb="sm"
            align="flex-end"
            style={{ maxWidth: '100%' }}>
        <FileUploadByImage user={userInDb} setUserForm={setUserInDb} />
        <Group mb="sm" style={{ flexDirection: 'column' , flex: 3}}>
            <TextInput
                label={t('settings.firstName')}
                placeholder={t('settings.firstNamePlaceholder')}
                value={userInDb.first_name}
                onChange={(e) =>
                setUserInDb({ ...userInDb, first_name: e.target.value })
                }
                mb="sm"
                required
                style={{ flex: 1, width: '100%' }}

            />
            <Text className={`text-input-counter ${userInDb.first_name.length > nameLength ? "error" : ""}`} style={{width: '100%' }}>
                {t('settings.characters', { count: userInDb.first_name.length, max: nameLength })}
            </Text>
            <TextInput
                label={t('settings.surname')}
                placeholder={t('settings.surnamePlaceholder')}
                value={userInDb.last_name}
                onChange={(e) =>
                setUserInDb({ ...userInDb, last_name: e.target.value })
                }
                mb="sm"
                required
                style={{ flex: 1, width: '100%' }}
            />
            <Text className={`text-input-counter ${userInDb.last_name.length > nameLength ? "error" : ""}` } style={{width: '100%' }}>
                {t('settings.characters', { count: userInDb.last_name.length, max: nameLength })}
            </Text>
        </Group>
      </Group>

      <Group grow mb="sm">
        <Stack>
          <TextInput
            label={t('settings.username')}
            placeholder={t('settings.usernamePlaceholder')}
            value={userInDb.username}
            onChange={(e) => {
                const value = e.target.value;
                setUserInDb({ ...userInDb, username: value });
            }}
            mb="sm"
            required
            disabled={true}
            maxLength={nameLength}
            />
            <Text className={`text-input-counter ${userInDb.username.length > nameLength ? "error" : ""}`}>
                {t('settings.characters', { count: userInDb.username.length, max: nameLength })}
            </Text>
          </Stack>
        </Group>

<Group grow mb="sm" align="flex-start">
  {/* Email field */}
  <Stack>
    <TextInput
      label={t('settings.email')}
      placeholder={t('settings.emailPlaceholder')}
      value={userInDb.email}
      onChange={(e) =>
        setUserInDb({ ...userInDb, email: e.target.value })
      }
      mb="sm"
      required
      error={
        (userInDb.email && !emailRegex.test(userInDb.email)
          ? t('settings.invalidEmailFormat')
          : undefined) || emailError
      }
      onBlur={() => {
        if (userInDb.email !== user?.email) {
          validateEmail(userInDb.email);
        }
      }}
      maxLength={nameLength}
    />
    <Text
      className={`text-input-counter ${
        userInDb.email.length > nameLength ? 'error' : ''
      }`}
    >
      {t('settings.characters', { count: userInDb.email.length, max: nameLength })}
    </Text>
  </Stack>

  {/* Phone number field using PhoneInput */}
  <Stack>
  <PhoneInput
    value={{
      country: (userInDb.phone_number || '').split(' ')[0] || '+55',
      phone: (userInDb.phone_number || '').split(' ').slice(1).join(' ') || '',
    }}
    onChange={(value: PhoneInputValue) =>
      setUserInDb({
        ...userInDb,
        phone_number: `${value.country} ${value.phone}`,
      })
    }
  />
  </Stack>
</Group>

        <Group grow mb="sm">
          <Stack>
            <TextInput
              label={t('settings.role')}
              placeholder={t('settings.rolePlaceholder')}
              value={userInDb.cargo}
              onChange={(e) =>
                  setUserInDb({ ...userInDb, cargo: e.target.value })
              }
              mb="sm"
              required
            />
            <Text className={`text-input-counter ${userInDb.cargo.length > nameLength ? "error" : ""}` }>
              {t('settings.characters', { count: userInDb.cargo.length, max: nameLength })}
            </Text>
          </Stack>
        </Group>

        <Group grow mb="sm">
          <Select
            label={t('settings.language')}
            placeholder={t('settings.languagePlaceholder')}
            value={userInDb.locale}
            onChange={(value) => {
              if (isAppLocale(value)) {
                setUserInDb({ ...userInDb, locale: value });
              }
            }}
            data={SUPPORTED_LOCALES.map((code) => ({
              value: code,
              label: LOCALE_LABELS[code],
            }))}
            withinPortal
          />
        </Group>

        <Switch
          label={t('settings.emailNotifications')}
          description={t('settings.emailNotificationsDescription')}
          checked={userInDb.email_notifications_enabled}
          onChange={(event) =>
            setUserInDb({
              ...userInDb,
              email_notifications_enabled: event.currentTarget.checked,
            })
          }
          mb="md"
        />


        {/* <Switch
            label="Status"
            checked={userInDb.status === 'active'}
            onChange={(e) =>
            setUserInDb({ ...userInDb, status: e.currentTarget.checked ? 'active' : 'inactive' })
            }
            mb="sm"
        /> */}
        { notificationMessage && notificationMessage.length > 0 && (
            <Text color= {emailSent ? 'green' : 'red'} >
              {notificationMessage}
            </Text>
        )}
      <Group
      spacing="md"
      mb="sm"
      align="flex-end"
      style={{ maxWidth: '100%' }}>
        <Button onClick={handleResetPassword} >{t('settings.resetPassword')}</Button>
      <Group position="right" mt="md" style={{ flex: 1 }}>
        <Button variant="default" onClick={onClose}>{t('settings.cancel')}</Button>
        <Button onClick={handleSave}>{t('settings.save')}</Button>
      </Group>
      </Group>

    </Modal>
  );
};
